import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { getAnalyticsGoals, updateAnalyticsGoals, type AnalyticsGoalsDto } from '@/lib/api/v2/settings'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent } from '@/components/ui/card'

export function AnalyticsGoalsSettings() {
  const [goals, setGoals] = useState<AnalyticsGoalsDto | null>(null)
  const [revenueTarget, setRevenueTarget] = useState('')
  const [salesTarget, setSalesTarget] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [isDirty, setIsDirty] = useState(false)

  useEffect(() => {
    getAnalyticsGoals()
      .then((data) => {
        setGoals(data)
        setRevenueTarget(data.monthlyRevenueTarget !== null ? String(data.monthlyRevenueTarget) : '')
        setSalesTarget(data.monthlySalesTarget !== null ? String(data.monthlySalesTarget) : '')
      })
      .catch(() => toast.error('Impossible de charger les objectifs analytiques.'))
  }, [])

  const handleSave = async () => {
    const parsedRevenue = revenueTarget.trim() === '' ? null : parseFloat(revenueTarget)
    const parsedSales = salesTarget.trim() === '' ? null : parseFloat(salesTarget)

    if (parsedRevenue !== null && (isNaN(parsedRevenue) || parsedRevenue < 0)) {
      toast.error("L'objectif CA mensuel doit être un nombre positif.")
      return
    }
    if (parsedSales !== null && (isNaN(parsedSales) || parsedSales < 0)) {
      toast.error("L'objectif ventes mensuelles doit être un nombre positif.")
      return
    }

    setIsSaving(true)
    try {
      const updated = await updateAnalyticsGoals({
        monthlyRevenueTarget: parsedRevenue,
        monthlySalesTarget: parsedSales,
      })
      setGoals(updated)
      setIsDirty(false)
      toast.success('Objectifs enregistrés.')
    } catch {
      toast.error("Erreur lors de l'enregistrement des objectifs.")
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Card>
      <CardContent className="pt-6 space-y-6">
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="monthly-revenue-target">Objectif CA mensuel (€)</Label>
            <p className="text-xs text-muted-foreground">
              Affiché comme ligne horizontale sur le graphique CA Timeline. Laisser vide pour masquer.
            </p>
            <Input
              id="monthly-revenue-target"
              type="number"
              min="0"
              step="100"
              placeholder="Ex. 50000"
              value={revenueTarget}
              onChange={(e) => { setRevenueTarget(e.target.value); setIsDirty(true) }}
              className="max-w-xs"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="monthly-sales-target">Objectif ventes mensuelles (€)</Label>
            <p className="text-xs text-muted-foreground">
              Comparé aux ventes signées du mois courant dans la carte KPI. Laisser vide pour masquer.
            </p>
            <Input
              id="monthly-sales-target"
              type="number"
              min="0"
              step="100"
              placeholder="Ex. 45000"
              value={salesTarget}
              onChange={(e) => { setSalesTarget(e.target.value); setIsDirty(true) }}
              className="max-w-xs"
            />
          </div>
        </div>

        <Button
          onClick={handleSave}
          disabled={isSaving || !isDirty || goals === null}
        >
          {isSaving ? 'Enregistrement…' : 'Enregistrer'}
        </Button>
      </CardContent>
    </Card>
  )
}
